import type { MaintenanceListTableFullData } from '@/components/sections/service/maintenance-list/table/full';
import {
  MaintenanceCostKey,
  MaintenancePartGroupsKey,
} from '@/types/services/contentstack/services/maintenance-service';
import { IIcon } from '@/types/shared';

export type CustomMaintenanceLcvModelImageData = {
  url: string;
  isMuX?: boolean;
  grades?: {
    [key: string]: string;
  };
  engines?: {
    [key: string]: string;
  };
};

export type CustomMaintenanceLcvModelImageMap = {
  pickup: {
    [key: string]: CustomMaintenanceLcvModelImageData;
  };
  ppv: {
    [key: string]: CustomMaintenanceLcvModelImageData;
  };
};

export enum SelectOptionStep {
  Model,
  Engine,
  Schedule,
}

export const INITIAL_SELECT_OPTION_VALUES = ['', '', ''];

export const CONTENT_FILTER_SELECTS: {
  key: string;
  placeholder: string;
}[] = [];

CONTENT_FILTER_SELECTS[SelectOptionStep.Model] = {
  key: 'model',
  placeholder: 'รุ่นรถ',
};
CONTENT_FILTER_SELECTS[SelectOptionStep.Engine] = {
  key: 'engine',
  placeholder: 'ขนาดเครื่องยนต์ / ระบบส่งกำลัง / ระบบขับเคลื่อน',
};
CONTENT_FILTER_SELECTS[SelectOptionStep.Schedule] = {
  key: 'schedule',
  placeholder: 'ระยะเวลาหรือระยะทาง',
};

const TABLE_VALUE_CELL_WIDTH = '90px';
const TABLE_SCROLL_BAR_SIZE = '8px';
const MAINTENANCE_TITLE: { [key: string]: string } = {
  [MaintenancePartGroupsKey.General]: 'ตรวจเช็กสภาพทั่วไป',
  [MaintenanceCostKey.CostBeforeVat]: 'ค่าใช้จ่าย (ไม่รวม VAT)',
  [MaintenanceCostKey.Discount]: 'คูปองส่วนลด/ ส่วนลดตรวจเช็ก',
  [MaintenanceCostKey.TotalCostWithVat]: 'ค่าใช้จ่ายทั้งหมด (รวม VAT)',
};
const MAINTENANCE_ICON: { [key: string]: IIcon } = {
  [MaintenancePartGroupsKey.General]: 'assignment',
  [MaintenanceCostKey.CostBeforeVat]: 'receipt',
  [MaintenanceCostKey.Discount]: 'gift-reward',
  [MaintenanceCostKey.TotalCostWithVat]: 'money',
};

export const TABLE_FULL_DATA: MaintenanceListTableFullData = {
  generalIcon: MAINTENANCE_ICON[MaintenancePartGroupsKey.General],
  generalTitle: MAINTENANCE_TITLE[MaintenancePartGroupsKey.General],
  headerLabelTitle: 'รายการเปลี่ยนถ่าย',
  headerLengthTitle:
    'ระยะทาง (กม.) / ระยะเวลา (เดือน) แล้วแต่อย่างใดอย่างหนึ่งจะถึงก่อน',
  tableCellWidth: TABLE_VALUE_CELL_WIDTH,
  tableScrollBarSize: TABLE_SCROLL_BAR_SIZE,
};

export const PRICE_DATA = {
  costBeforeVatTitle: MAINTENANCE_TITLE[MaintenanceCostKey.CostBeforeVat],
  discountTitle: MAINTENANCE_TITLE[MaintenanceCostKey.Discount],
  totalCostWithVatTitle: MAINTENANCE_TITLE[MaintenanceCostKey.TotalCostWithVat],
  costBeforeVatIcon: MAINTENANCE_ICON[MaintenanceCostKey.CostBeforeVat],
  discountIcon: MAINTENANCE_ICON[MaintenanceCostKey.Discount],
  totalCostWithVatIcon: MAINTENANCE_ICON[MaintenanceCostKey.TotalCostWithVat],
};

export const DEFAULT_TITLE = 'รายการบำรุงรักษา<span>ตามระยะ</span>';
export const DEFAULT_DESCRIPTION =
  'ค้นหารายการบำรุงรักษา<span>ตามระยะของคุณ</span>';
export const DEFAULT_MOBLE_BG =
  'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltc76553c290cf0099/62de2b033bba407090b23939/bg-m.jpg';
export const DEFAULT_DESKTOP_BG =
  'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt548b9d8cff19c4a5/62de2b036ab9c47806f8c7d4/bg.jpg';

export const CUSTOM_MAP_OLD_LCV_MODEL_QUERY = {
  'isuzu-d-max-e5': 'pickup-2024-euro5',
  'all-new-isuzu-d-max': 'pickup-2020',
  'isuzu-d-max-bluepower-2016-2019': 'pickup-2016',
  'isuzu-mu-x-e5': 'ppv-2025-euro5',
  'all-new-isuzu-mu-x': 'ppv-2020',
  'isuzu-mu-x-bluepower-2019-2020': 'ppv-2019',
  'isuzu-mu-x-bluepower-2016-2018': 'ppv-2016',
};
export const CUSTOM_MAP_OLD_LCV_ENGINE_QUERY = {
  '30-mt-4x4-esc': '30-mt-4x4',
  '19-at-4x2-mux_active': '19-at-4x2-active',
  '19-at-4x2-mux_elegant': '19-at-4x2-elegant',
  '19-at-4x2-mux_ultimate': '19-at-4x2-ultimate',
  '30-at-4x2-mux_elegant': '30-at-4x2-elegant',
  '30-at-4x2-mux_ultimate': '30-at-4x2-ultimate',
  '30-at-4x2-mux_rs': '30-at-4x2-rs',
  '30-at-4x4-mux_rs': '30-at-4x4-rs',
};

export const DEFAULT_LCV_VEHICLE_IMAGE =
  'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blta5ac237e4e8755fe/62df4c64df0a3871e08a41c8/lcv-compare-default.jpg?format=pjpg&auto=webp&width=320&quality=85';

export const CUSTOM_LCV_MODEL_IMAGE_MAP: CustomMaintenanceLcvModelImageMap = {
  pickup: {
    '2025': {
      url: '',
      engines: {
        '19mhev':
          'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt01475f197ab1e656/67077efa2273f249e5b4d770/hilander-mhev-front-dolomite-white-pearl.png',
        '22': 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltce2cc56009ddcfd0/67344cbe90cfa3152bfda696/4door_hilander-2-2-ddi-m-at_elbrus-gray-opaque_front.png',
        '30': 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltf991bba2d628f59f/6732ec594ef104e300e56135/vcross_3-0-ddi-m-4-door-at_elbrus-gray-opaque_front.png',
      },
    },
    '2024': {
      url: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt35ab67310f8b23e4/65266c5b6b6b83f80d76c277/v-cross_2024.png',
      engines: {
        '19mhev':
          'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt01475f197ab1e656/67077efa2273f249e5b4d770/hilander-mhev-front-dolomite-white-pearl.png',
        '19': 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltd73ac129366c8158/651f74de6e43119816ed3f24/hi-lander-4-door-1-9-ddi-m-dolomite-pearl-white.png',
        '30': 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt35ab67310f8b23e4/65266c5b6b6b83f80d76c277/v-cross_2024.png',
      },
    },
    '2021': {
      url: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt35ab67310f8b23e4/65266c5b6b6b83f80d76c277/v-cross_2024.png',
    },
    '2020': {
      url: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt35ab67310f8b23e4/65266c5b6b6b83f80d76c277/v-cross_2024.png',
    },
    '2016': {
      url: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltda39cdb3f9a80d9f/62ea3c5ce71af410b83bf028/isuzu-d-max-bluepower-2016-2019.png',
    },
  },
  ppv: {
    '2025': {
      url: '',
      isMuX: true,
      grades: {
        active:
          'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt24b3b645b5a748e3/666925774969e4c25f858154/mu-x-front-active-dolomite-white-pearl.png',
        elegant:
          'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt6299ceda63144320/66692835664d45f1fe20dc41/mu-x-front-elegant-eiger-gray-metallic.png',
        ultimate:
          'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltc7c564dec4a2575a/66692aca46a03792daa26408/mu-x-front-ultimate-eiger-gray-metallic.png',
        rs: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt95d5d1008250eead/66692ca146a0375d52a26414/mu-x-front-rs-eiger-grey.png',
      },
    },
    '2021': {
      url: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt623e8104e73821e6/63329cd5e0614a3410c5c03c/all-new-isuzu-mu-x.png',
    },
    '2020': {
      url: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt623e8104e73821e6/63329cd5e0614a3410c5c03c/all-new-isuzu-mu-x.png',
    },
    '2019': {
      url: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltf9c8e9395e81943d/62ea3c9042380217691bd72a/isuzu-mu-x-bluepower-2019-2020.png',
    },
    '2016': {
      url: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltb74ebd2720a0f80e/62ea3c70d3b8a57004565f2b/isuzu-mu-x-bluepower-2016-2018.png',
    },
  },
};

/**
 * Internal note for maintaining previous URL query
 */
// const OLD_LCV_MODELS = [
//   'isuzu-d-max-e5',
//   'all-new-isuzu-d-max',
//   'isuzu-d-max-bluepower-2016-2019',
//   'isuzu-mu-x-e5',
//   'all-new-isuzu-mu-x',
//   'isuzu-mu-x-bluepower-2019-2020',
//   'isuzu-mu-x-bluepower-2016-2018',
// ];
// const OLD_LCV_ENGINES = [
//   '19-at-4x2',
//   '19-mt-4x2',
//   '30-at-4x2',
//   '30-mt-4x2',
//   '30-at-4x4',
//   '30-mt-4x4',
//   '30-mt-4x4-esc',
//   '19-at-4x2-mux_active',
//   '19-at-4x2-mux_elegant',
//   '19-at-4x2-mux_ultimate',
//   '30-at-4x2-mux_elegant',
//   '30-at-4x2-mux_ultimate',
//   '30-at-4x2-mux_rs',
//   '30-at-4x4-mux_rs',
// ];
// const CURRENT_POSSIBLE_URLS = [
//   'ppv-2025-euro5_30-at-4x4-rs',
//   'ppv-2025-euro5_30-at-4x2-rs',
//   'ppv-2025-euro5_30-at-4x2-ultimate',
//   'ppv-2025-euro5_30-at-4x2-elegant',
//   'ppv-2025-euro5_19-at-4x2-ultimate',
//   'ppv-2025-euro5_19-at-4x2-elegant',
//   'ppv-2025-euro5_19-at-4x2-active',
//   'pickup-2024-euro5_19-mt-4x2',
//   'pickup-2024-euro5_19-at-4x2',
//   'pickup-2024-euro5_30-mt-4x4',
//   'pickup-2024-euro5_30-mt-4x2',
//   'pickup-2024-euro5_30-at-4x4',
//   'pickup-2024-euro5_30-at-4x2',
//   'ppv-2020_19-mt-4x2',
//   'ppv-2020_19-at-4x2',
//   'ppv-2020_30-at-4x4',
//   'ppv-2020_30-at-4x2',
//   'pickup-2020_19-mt-4x2',
//   'pickup-2020_19-at-4x2',
//   'pickup-2020_30-mt-4x4',
//   'pickup-2020_30-mt-4x2',
//   'pickup-2020_30-at-4x4',
//   'pickup-2020_30-at-4x2',
//   'ppv-2019_19-mt-4x2',
//   'ppv-2019_19-at-4x2',
//   'ppv-2019_30-at-4x4',
//   'ppv-2019_30-at-4x2',
//   'ppv-2016_19-mt-4x2',
//   'ppv-2016_19-at-4x2',
//   'ppv-2016_30-at-4x4',
//   'ppv-2016_30-at-4x2',
//   'pickup-2016_19-mt-4x2',
//   'pickup-2016_19-at-4x2',
//   'pickup-2016_30-mt-4x4',
//   'pickup-2016_30-mt-4x2',
//   'pickup-2016_30-at-4x4',
//   'pickup-2016_30-at-4x2',
// ];
